/** Live canvas state for one mount, held over the push transport. Never polled. */

import type { CueFrame } from "@nebulize/sdk";
import { useEffect, useRef, useState } from "react";

import type { FrameStamp } from "./canvasFrames";
import type { CanvasStreamOptions } from "./canvasStream";
import { openCanvasStream } from "./canvasStream";
import type { CanvasState } from "./runtime";

export interface Polled {
  state: CanvasState | null;
  error: string | null;
  /** Place in the admission queue, or null once admitted. Never an error: the socket is open. */
  waitingPosition: number | null;
  /** The last frame that named its own send instant, which a press counts from. RUN-039. */
  frame: FrameStamp | undefined;
  /** The last cue this mount fired. A canvas plays each `envelope_id` once and drops the rest. */
  cue: CueFrame | undefined;
}

/**
 * `stream` is a test seam for an injected socket factory. A real page never passes it. Read
 * through a ref rather than a dependency, so an unstable reference across renders never reopens
 * the connection on its own.
 */
export function useCanvasState(
  mountId: string,
  stream?: CanvasStreamOptions,
  asCanvas?: string,
): Polled {
  const [state, setState] = useState<CanvasState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [waitingPosition, setWaitingPosition] = useState<number | null>(null);
  const [frame, setFrame] = useState<FrameStamp | undefined>(undefined);
  const [cue, setCue] = useState<CueFrame | undefined>(undefined);
  const streamRef = useRef(stream);
  streamRef.current = stream;

  useEffect(() => {
    setState(null);
    setError(null);
    setWaitingPosition(null);
    setFrame(undefined);
    setCue(undefined);
    const connection = openCanvasStream(
      mountId,
      {
        onState: (next, stamp) => {
          setState(next);
          setFrame(stamp);
        },
        onCue: setCue,
        onError: setError,
        onWaiting: setWaitingPosition,
      },
      asCanvas === undefined
        ? streamRef.current
        : { ...streamRef.current, asCanvas },
    );
    return () => {
      connection.close();
    };
  }, [mountId, asCanvas]);

  return { state, error, waitingPosition, frame, cue };
}
